import React, { useEffect, useMemo, useRef, useState } from "react";
import {
	CalendarIcon,
	MagnifyingGlassIcon,
	XMarkIcon,
	EyeIcon,
	PlusIcon,
	PencilSquareIcon,
	TrashIcon,
} from "@heroicons/react/24/outline";
import { BiTrip } from "react-icons/bi";
import AssignModal from "../Seat/AssignModal";
import { useBusStore, buildBusSeatStatus } from "../../../store/busStore";
import { useEventStore } from "../../../store/eventStore";
import { useSeatmapStore } from "../../../store/seatmapStore";

const DEFAULT_SEAT_COUNT = 40;

const buildEventMap = (events) => {
	const map = {};
	(events || []).forEach((event) => {
		const key = `${event.ev_ym}-${event.ev_seq}`;
		map[key] = event;
	});
	return map;
};

const formatDate = (value) => {
	if (!value) return "-";
	if (value.includes("-")) return value;
	if (value.length === 8) {
		return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6)}`;
	}
	return value;
};

const normalizeTrainTypeCode = (code) => {
	const normalized = String(code || "").trim();
	if (!normalized) return "";
	if (normalized.startsWith("BUS_")) return normalized.replace("_", "");
	return normalized;
};

const formatSeatmapLabel = (layout) => {
	const typeCode =
		normalizeTrainTypeCode(layout?.train_type_code) ||
		String(layout?.train_type_name || "").trim();
	const name = String(layout?.name || "").trim();
	const variant = layout?.variant ? ` (${layout.variant})` : "";
	if (typeCode && name) return `${typeCode} - ${name}${variant}`;
	if (name) return `${name}${variant}`;
	if (typeCode) return `${typeCode}${variant}`;
	return "좌석배치도";
};

const buildDefaultForm = (searchDate) => ({
	bus_code: "",
	ev_ym: "",
	ev_seq: "",
	service_date: searchDate || "",
	seatmap_layout_id: "",
	car_number: "1",
	seat_count: String(DEFAULT_SEAT_COUNT),
	route: "",
	memo: "",
});

export default function BusInfo() {
	const {
		listServices,
		getSeats,
		getSeatmap,
		createService,
		updateService,
		deleteService,
	} = useBusStore();
	const { getEventsByDate, getReservationInfo } = useEventStore();
	const { layouts, fetchLayouts } = useSeatmapStore();

	const [services, setServices] = useState([]);
	const [registerEvents, setRegisterEvents] = useState([]);
	const [isRegisterEventLoading, setIsRegisterEventLoading] = useState(false);
	const [eventMap, setEventMap] = useState({});
	const [searchDate, setSearchDate] = useState("");
	const [busCode, setBusCode] = useState("");
	const [evYm, setEvYm] = useState("");
	const [evSeq, setEvSeq] = useState("");
	const [localLoading, setLocalLoading] = useState(false);
	const [error, setError] = useState(null);
	const [loadingBusCode, setLoadingBusCode] = useState(null);
	const [isSaving, setIsSaving] = useState(false);

	const [isViewModalOpen, setIsViewModalOpen] = useState(false);
	const [viewSeatData, setViewSeatData] = useState(null);
	const [viewSeatLayout, setViewSeatLayout] = useState(null);
	const [viewAssignmentData, setViewAssignmentData] = useState(null);
	const initialLoadRef = useRef(true);

	const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
	const [editingService, setEditingService] = useState(null);
	const [formData, setFormData] = useState(buildDefaultForm(""));

	useEffect(() => {
		const today = new Date().toISOString().split("T")[0];
		setSearchDate(today);
	}, []);

	useEffect(() => {
		if (initialLoadRef.current && searchDate) {
			handleSearch();
			initialLoadRef.current = false;
		}
	}, [searchDate]);

	useEffect(() => {
		if (isRegisterModalOpen) {
			fetchLayouts({ category: "bus" });
		}
	}, [isRegisterModalOpen, fetchLayouts]);

	useEffect(() => {
		if (!isRegisterModalOpen) return;
		if (!formData.service_date) {
			setRegisterEvents([]);
			return;
		}

		let isCancelled = false;
		const formattedDate = formData.service_date.replace(/-/g, "");

		const loadEvents = async () => {
			setIsRegisterEventLoading(true);
			try {
				const response = await getEventsByDate(formattedDate, formattedDate);
				if (!isCancelled) {
					setRegisterEvents(response.success ? response.data || [] : []);
				}
			} catch (err) {
				if (!isCancelled) {
					console.error("행사 목록 로드 실패:", err);
					setRegisterEvents([]);
				}
			} finally {
				if (!isCancelled) {
					setIsRegisterEventLoading(false);
				}
			}
		};

		loadEvents();

		return () => {
			isCancelled = true;
		};
	}, [isRegisterModalOpen, formData.service_date, getEventsByDate]);

	const busLayouts = useMemo(() => {
		return (layouts || []).filter((layout) => {
			const category = String(layout.train_type_category || "").toLowerCase();
			if (category) {
				return category === "bus";
			}
			return String(layout.train_type_code || "").toUpperCase().includes("BUS");
		});
	}, [layouts]);

	const eventOptions = useMemo(() => {
		return (registerEvents || []).map((event) => ({
			key: `${event.ev_ym}-${event.ev_seq}`,
			label: `${event.ev_nm || ""} (${event.ev_ym}-${event.ev_seq})`,
			value: event,
		}));
	}, [registerEvents]);

	const selectedEventKey =
		formData.ev_ym && formData.ev_seq
			? `${formData.ev_ym}-${formData.ev_seq}`
			: "";

	const handleClearDate = () => {
		setSearchDate("");
	};

	const handleSearch = async () => {
		setLocalLoading(true);
		setError(null);

		const params = {};
		const formattedDate = searchDate ? searchDate.replace(/-/g, "") : "";
		if (formattedDate) {
			params.service_date = formattedDate;
		}
		if (busCode.trim()) {
			params.bus_code = busCode.trim();
		}
		if (evYm.trim()) {
			params.ev_ym = evYm.trim();
		}
		const evSeqValue = parseInt(evSeq, 10);
		if (Number.isInteger(evSeqValue)) {
			params.ev_seq = evSeqValue;
		}

		try {
			const [serviceResponse, eventResponse] = await Promise.all([
				listServices(params),
				formattedDate
					? getEventsByDate(formattedDate, formattedDate)
					: Promise.resolve({ success: false, data: [] }),
			]);

			if (serviceResponse.success) {
				setServices(serviceResponse.data || []);
			} else {
				setServices([]);
				setError(serviceResponse.error || "버스 좌석 등록 조회 실패");
			}

			if (eventResponse.success) {
				setEventMap(buildEventMap(eventResponse.data || []));
			} else {
				setEventMap({});
			}
		} catch (err) {
			console.error("버스 좌석 등록 조회 실패:", err);
			setError("버스 좌석 등록 조회 중 오류가 발생했습니다.");
			setServices([]);
			setEventMap({});
		} finally {
			setLocalLoading(false);
		}
	};

	const handleViewSeatmap = async (service) => {
		if (!service?.bus_code) return;

		setLoadingBusCode(service.bus_code);
		try {
			const [seatsResponse, reservationResponse, seatmapResponse] =
				await Promise.all([
					getSeats(service.bus_code),
					getReservationInfo(service.ev_ym, service.ev_seq, "go"),
					getSeatmap(service.bus_code, service.seatmap_layout_id, null),
				]);

			const reservations = reservationResponse.success
				? reservationResponse.data
				: [];
			const seatmapLayout = seatmapResponse.success
				? seatmapResponse.seatmapLayout
				: null;
			const seatStatus = seatsResponse.success
				? buildBusSeatStatus(seatsResponse.data?.items || [], reservations, service.car_number, seatmapLayout)
				: {};

			const eventKey = `${service.ev_ym}-${service.ev_seq}`;
			const eventName = eventMap[eventKey]?.ev_nm || "";
			const eventData = {
				ev_ym: service.ev_ym,
				ev_seq: service.ev_seq,
				ev_nm: eventName,
				start_day: service.service_date,
			};

			setViewSeatData(seatStatus);
			setViewSeatLayout(seatmapLayout);
			setViewAssignmentData({
				eventData,
				busService: service,
				seatmapLayout,
			});
			setIsViewModalOpen(true);
		} catch (err) {
			console.error("버스 좌석 정보 조회 실패:", err);
			alert("버스 좌석 정보를 불러오지 못했습니다.");
		} finally {
			setLoadingBusCode(null);
		}
	};

	const handleOpenRegister = (service) => {
		if (service) {
			setEditingService(service);
			setFormData({
				bus_code: service.bus_code || "",
				ev_ym: service.ev_ym || "",
				ev_seq: String(service.ev_seq ?? ""),
				service_date: formatDate(service.service_date || ""),
				seatmap_layout_id: service.seatmap_layout_id
					? String(service.seatmap_layout_id)
					: "",
				car_number: String(service.car_number ?? "1"),
				seat_count: String(service.seat_count || DEFAULT_SEAT_COUNT),
				route: service.route || "",
				memo: service.memo || "",
			});
		} else {
			setEditingService(null);
			setFormData(buildDefaultForm(searchDate));
		}
		setIsRegisterModalOpen(true);
	};

	const handleCloseRegister = () => {
		setIsRegisterModalOpen(false);
		setEditingService(null);
		setFormData(buildDefaultForm(searchDate));
		setRegisterEvents([]);
		setIsRegisterEventLoading(false);
	};

	const selectedLayoutCars = useMemo(() => {
		if (!formData.seatmap_layout_id) return [];
		const layout = busLayouts.find((l) => String(l.id) === formData.seatmap_layout_id);
		return layout?.cars || [];
	}, [formData.seatmap_layout_id, busLayouts]);

	const handleFormChange = (field, value) => {
		if (field === "seatmap_layout_id") {
			const selectedLayout = busLayouts.find((l) => String(l.id) === value);
			const cars = selectedLayout?.cars || [];
			if (cars.length > 0) {
				const firstCar = cars[0];
				setFormData((prev) => ({
					...prev,
					seatmap_layout_id: value,
					car_number: String(firstCar.car_number),
					seat_count: String(firstCar.seat_count || DEFAULT_SEAT_COUNT),
				}));
				return;
			}
			setFormData((prev) => ({
				...prev,
				seatmap_layout_id: value,
				car_number: "1",
				seat_count: String(DEFAULT_SEAT_COUNT),
			}));
			return;
		}
		if (field === "car_number" && selectedLayoutCars.length > 0) {
			const car = selectedLayoutCars.find((c) => String(c.car_number) === value);
			if (car) {
				setFormData((prev) => ({
					...prev,
					car_number: value,
					seat_count: String(car.seat_count || DEFAULT_SEAT_COUNT),
				}));
				return;
			}
		}
		setFormData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleSelectEvent = (eventKey) => {
		const selected = eventOptions.find((option) => option.key === eventKey);
		if (!selected) return;
		const event = selected.value;
		setFormData((prev) => ({
			...prev,
			ev_ym: event.ev_ym,
			ev_seq: String(event.ev_seq ?? ""),
			service_date: formatDate(event.start_day || prev.service_date || ""),
		}));
	};

	const handleSaveRegister = async () => {
		const trimmedBusCode = formData.bus_code.trim();
		const trimmedEvYm = formData.ev_ym.trim();
		const trimmedEvSeq = formData.ev_seq.trim();
		if (!editingService && !trimmedBusCode) {
			alert("버스 코드를 입력해주세요.");
			return;
		}
		if (!trimmedEvYm || trimmedEvYm.length !== 6) {
			alert("행사 코드(YYYYMM)를 입력해주세요.");
			return;
		}
		const evSeqValue = parseInt(trimmedEvSeq, 10);
		if (!Number.isInteger(evSeqValue)) {
			alert("행사 SEQ를 입력해주세요.");
			return;
		}
		if (!formData.service_date) {
			alert("운행일자를 입력해주세요.");
			return;
		}

		const seatmapLayoutId = formData.seatmap_layout_id
			? Number(formData.seatmap_layout_id)
			: null;

		setIsSaving(true);
		try {
			if (editingService) {
				const payload = {
					ev_ym: trimmedEvYm,
					ev_seq: evSeqValue,
					service_date: formData.service_date,
					seatmap_layout_id: seatmapLayoutId,
					car_number: Number(formData.car_number) || 1,
					seat_count: Number(formData.seat_count) || DEFAULT_SEAT_COUNT,
					route: formData.route.trim() || null,
					memo: formData.memo.trim() || null,
				};
				const result = await updateService(editingService.bus_code, payload);
				if (!result.success) {
					alert(result.error || "버스 좌석 등록 수정에 실패했습니다.");
					return;
				}
				alert("버스 좌석 등록이 수정되었습니다.");
			} else {
				const payload = {
					bus_code: trimmedBusCode,
					ev_ym: trimmedEvYm,
					ev_seq: evSeqValue,
					service_date: formData.service_date,
					seatmap_layout_id: seatmapLayoutId,
					car_number: Number(formData.car_number) || 1,
					seat_count: Number(formData.seat_count) || DEFAULT_SEAT_COUNT,
					route: formData.route.trim() || null,
					memo: formData.memo.trim() || null,
				};
				const result = await createService(payload);
				if (!result.success) {
					alert(result.error || "버스 좌석 등록에 실패했습니다.");
					return;
				}
				alert("버스 좌석 등록이 완료되었습니다.");
			}

			handleCloseRegister();
			await handleSearch();
		} catch (err) {
			console.error("버스 좌석 등록 처리 실패:", err);
			alert("버스 좌석 등록 처리 중 오류가 발생했습니다.");
		} finally {
			setIsSaving(false);
		}
	};

	const handleDeleteRegister = async (service) => {
		if (!service?.bus_code) return;
		if (!window.confirm("해당 버스 좌석 등록을 삭제하시겠습니까?")) {
			return;
		}
		setLoadingBusCode(service.bus_code);
		try {
			const result = await deleteService(service.bus_code);
			if (!result.success) {
				alert(result.error || "버스 좌석 등록 삭제에 실패했습니다.");
				return;
			}
			alert("버스 좌석 등록이 삭제되었습니다.");
			await handleSearch();
		} catch (err) {
			console.error("버스 좌석 등록 삭제 실패:", err);
			alert("버스 좌석 등록 삭제 중 오류가 발생했습니다.");
		} finally {
			setLoadingBusCode(null);
		}
	};

	const resolveEventLabel = (service) => {
		const key = `${service.ev_ym}-${service.ev_seq}`;
		return eventMap[key]?.ev_nm || key;
	};

	const DataBox = (
		<div className="overflow-x-auto">
			<table className="min-w-full divide-y divide-gray-200">
				<thead className="bg-gray-50">
					<tr>
						<th className="pl-6 pr-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							번호
						</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							버스 코드
						</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							행사
						</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							운행일
						</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							호차
						</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							좌석수
						</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							배정/잔여
						</th>
						<th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
							상태
						</th>
						<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
							관리
						</th>
						<th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
							좌석보기
						</th>
					</tr>
				</thead>
				<tbody className="bg-white divide-y divide-gray-200">
					{services.length > 0 ? (
						services.map((service, index) => (
							<tr key={service.bus_code}>
								<td className="pl-6 pr-3 py-3 whitespace-nowrap text-sm text-gray-500">
									{index + 1}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
									{service.bus_code}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
									{resolveEventLabel(service)}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
									{formatDate(service.service_date)}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
									{service.car_number ?? 1}호차
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
									{service.total_seats || service.seat_count || 0}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
									{service.assigned_seats || 0} / {service.available_seats || 0}
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-sm">
									<span
										className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
											service.is_assigned
												? "bg-green-100 text-green-800"
												: "bg-yellow-100 text-yellow-800"
										}`}
									>
										{service.is_assigned ? "확정" : "진행중"}
									</span>
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-right">
									<div className="inline-flex items-center gap-2">
										<button
											onClick={() => handleOpenRegister(service)}
											className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800"
										>
											<PencilSquareIcon className="h-4 w-4" />
											수정
										</button>
										<button
											onClick={() => handleDeleteRegister(service)}
											disabled={loadingBusCode === service.bus_code}
											className="inline-flex items-center gap-1 text-sm text-red-500 hover:text-red-700 disabled:opacity-50"
										>
											<TrashIcon className="h-4 w-4" />
											삭제
										</button>
									</div>
								</td>
								<td className="px-3 py-3 whitespace-nowrap text-right">
									<button
										onClick={() => handleViewSeatmap(service)}
										disabled={loadingBusCode === service.bus_code}
										className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
									>
										{loadingBusCode === service.bus_code ? (
											"불러오는 중..."
										) : (
											<>
												<EyeIcon className="h-4 w-4" />
												보기
											</>
										)}
									</button>
								</td>
							</tr>
						))
					) : (
						<tr>
							<td colSpan="9" className="px-3 py-8 text-center text-sm text-gray-500">
								{error ? (
									<div className="text-red-500">
										<p>오류가 발생했습니다.</p>
										<p>{error}</p>
									</div>
								) : (
									<div className="flex flex-col items-center">
										<BiTrip className="h-10 w-10 text-gray-400 mb-2" />
										<p>조회된 버스 좌석 등록이 없습니다.</p>
										<p className="text-sm">조건을 입력하고 검색해 주세요.</p>
									</div>
								)}
							</td>
						</tr>
					)}
				</tbody>
			</table>
		</div>
	);

	return (
		<div className="py-6">
			<h1 className="text-2xl font-bold text-gray-900 mb-6">
				버스 좌석 등록
			</h1>
			<p className="text-gray-600 mb-4">
				버스 좌석 등록과 배정 현황을 관리합니다.
			</p>

			<div className="bg-white shadow rounded-lg p-6 mb-6">
				<div className="flex flex-wrap items-end gap-4">
					<div className="relative flex items-center">
						<label htmlFor="searchDate" className="mr-2 text-sm font-medium text-gray-700">
							운행일자
						</label>
						<div className="relative">
							<input
								type="date"
								id="searchDate"
								value={searchDate}
								onChange={(e) => setSearchDate(e.target.value)}
								className="pl-10 pr-8 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
							/>
							<CalendarIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
							{searchDate && (
								<button
									onClick={handleClearDate}
									className="absolute right-2 top-1/2 -translate-y-1/2"
								>
									<XMarkIcon className="h-4 w-4 text-gray-400" />
								</button>
							)}
						</div>
					</div>

					<div>
						<label htmlFor="busCode" className="block text-sm font-medium text-gray-700">
							버스 코드
						</label>
						<input
							id="busCode"
							value={busCode}
							onChange={(e) => setBusCode(e.target.value)}
							className="mt-1 w-40 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
							placeholder="BUS-001"
						/>
					</div>

					<div>
						<label className="block text-sm font-medium text-gray-700">행사 코드</label>
						<div className="mt-1 flex gap-2">
							<input
								value={evYm}
								onChange={(e) => setEvYm(e.target.value)}
								className="w-28 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
								placeholder="YYYYMM"
							/>
							<input
								value={evSeq}
								onChange={(e) => setEvSeq(e.target.value)}
								className="w-20 border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
								placeholder="SEQ"
							/>
						</div>
					</div>

					<button
						onClick={handleSearch}
						disabled={localLoading}
						className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-md text-sm shadow-sm hover:shadow-md transition disabled:opacity-50"
					>
						<div className="flex items-center">
							{localLoading ? (
								<>검색 중...</>
							) : (
								<>
									<MagnifyingGlassIcon className="h-4 w-4 mr-1" />
									검색하기
								</>
							)}
						</div>
					</button>

					<button
						onClick={() => handleOpenRegister(null)}
						className="inline-flex items-center gap-1 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700"
					>
						<PlusIcon className="h-4 w-4" />
						버스 좌석 등록
					</button>
				</div>
			</div>

			<div className="bg-white shadow rounded-lg overflow-hidden">
				{localLoading ? (
					<div className="flex justify-center items-center p-12">
						<div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
					</div>
				) : (
					DataBox
				)}
			</div>

			{isRegisterModalOpen && (
				<div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true">
					<div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
						<div
							className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
							aria-hidden="true"
							onClick={handleCloseRegister}
						></div>

						<span
							className="hidden sm:inline-block sm:align-middle sm:h-screen"
							aria-hidden="true"
						>
							&#8203;
						</span>

						<div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
							<div className="bg-gray-50 px-4 py-3 border-b border-gray-200 sm:px-6 flex justify-between items-center">
								<div>
									<h3 className="text-lg leading-6 font-medium text-gray-900">
										{editingService ? "버스 좌석 등록 수정" : "버스 좌석 등록"}
									</h3>
									<p className="text-sm text-gray-500">
										좌석 배정을 시작하기 위한 버스 정보를 등록합니다.
									</p>
								</div>
								<button
									type="button"
									className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
									onClick={handleCloseRegister}
								>
									<span className="sr-only">닫기</span>
									<XMarkIcon className="h-6 w-6" aria-hidden="true" />
								</button>
							</div>

							<div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									<div>
										<label className="block text-sm font-medium text-gray-700">
											버스 코드
										</label>
										<input
											value={formData.bus_code}
											disabled={Boolean(editingService)}
											onChange={(e) => handleFormChange("bus_code", e.target.value)}
											className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
											placeholder="BUS-001"
										/>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700">
											운행일자
										</label>
										<input
											type="date"
											value={formData.service_date}
											onChange={(e) => handleFormChange("service_date", e.target.value)}
											className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
										/>
									</div>

									<div className="md:col-span-2">
										<label className="block text-sm font-medium text-gray-700">
											행사 선택
										</label>
										<select
											value={selectedEventKey}
											onChange={(e) => handleSelectEvent(e.target.value)}
											className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
										>
											<option value="">행사를 선택하세요</option>
											{eventOptions.map((option) => (
												<option key={option.key} value={option.key}>
													{option.label}
												</option>
											))}
										</select>
										{isRegisterEventLoading ? (
											<p className="mt-1 text-xs text-gray-500">
												행사 목록을 불러오는 중입니다.
											</p>
										) : eventOptions.length === 0 ? (
											<p className="mt-1 text-xs text-gray-500">
												해당 날짜 행사 목록이 없습니다.
											</p>
										) : null}
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700">
											좌석배치도
										</label>
										<select
											value={formData.seatmap_layout_id}
											onChange={(e) => handleFormChange("seatmap_layout_id", e.target.value)}
											className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
										>
											<option value="">기본 4열 배치</option>
											{busLayouts.map((layout) => (
												<option key={layout.id} value={layout.id}>
													{formatSeatmapLabel(layout)}
													{layout.car_count > 0
														? ` (${layout.car_count}대, 총 ${layout.total_seat_count}석)`
														: ""}
												</option>
											))}
										</select>
										{busLayouts.length === 0 && (
											<p className="mt-1 text-xs text-gray-500">
												등록된 버스 좌석배치도가 없습니다.
											</p>
										)}
									</div>

									<div className="grid grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700">
												호차
											</label>
											{selectedLayoutCars.length > 0 ? (
												<select
													value={formData.car_number}
													onChange={(e) => handleFormChange("car_number", e.target.value)}
													className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
												>
													{selectedLayoutCars.map((car) => (
														<option key={car.car_number} value={car.car_number}>
															{car.car_number}호차 ({car.seat_count}석)
														</option>
													))}
												</select>
											) : (
												<input
													type="number"
													min="1"
													value={formData.car_number}
													onChange={(e) => handleFormChange("car_number", e.target.value)}
													className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
												/>
											)}
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700">
												좌석수
											</label>
											<input
												type="number"
												min="1"
												value={formData.seat_count}
												onChange={(e) => handleFormChange("seat_count", e.target.value)}
												disabled={selectedLayoutCars.length > 0}
												className={`mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500 ${selectedLayoutCars.length > 0 ? "bg-gray-100" : ""}`}
											/>
										</div>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700">
											노선 메모
										</label>
										<input
											value={formData.route}
											onChange={(e) => handleFormChange("route", e.target.value)}
											className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
											placeholder="예) 서울-강릉"
										/>
									</div>

									<div className="md:col-span-2">
										<label className="block text-sm font-medium text-gray-700">
											메모
										</label>
										<textarea
											value={formData.memo}
											onChange={(e) => handleFormChange("memo", e.target.value)}
											className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-blue-500 focus:border-blue-500"
											rows="3"
											placeholder="특이사항을 입력하세요"
										/>
									</div>
								</div>
							</div>

							<div className="bg-gray-50 px-4 py-3 border-t border-gray-200 sm:px-6 flex justify-end gap-2">
								<button
									onClick={handleCloseRegister}
									className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
								>
									취소
								</button>
								<button
									onClick={handleSaveRegister}
									disabled={isSaving}
									className="px-4 py-2 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
								>
									{isSaving ? "저장 중..." : "저장"}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			<AssignModal
				isOpen={isViewModalOpen}
				onClose={() => {
					setIsViewModalOpen(false);
					setViewSeatData(null);
					setViewSeatLayout(null);
					setViewAssignmentData(null);
				}}
				readOnly
				assignmentData={viewAssignmentData}
				initialSeatData={viewSeatData}
				initialSeatLayout={viewSeatLayout}
			/>
		</div>
	);
}
